import React, { useCallback, useMemo, useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import PropTypes from 'prop-types';

import { endOfDay, endOfToday, format, parse, startOfDay } from 'date-fns';

import { periodFromDate } from '@ecp/common/src/utils/dateUtils';
import useEventCallback from '@ecp/common/src/hooks/useEventCallback';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import { FlexBox, FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import Dropdown from '@ecp/common/src/components/dropdown/bo/Dropdown';
import Input from '@ecp/common/src/components/input/bo/Input';
import PeriodSearch from '@ecp/common/src/components/periodSearch/bo/PeriodSearch';
import CheckboxGroupDropdown from '@ecp/common/src/components/checkboxGroupDropdown/CheckboxGroupDropdown';
import Popup from '@ecp/common/src/components/modal/bo/Popup';
import AllCheckboxGroup from '@ecp/common/src/components/checkbox/AllCheckboxGroup';

import { userInfoState } from '@bo-recoil/common/user/atom';
import { useAlertDialog } from '@bo-hooks/common/useModal';
import { useCategoryList } from '@bo-hooks/promotion/useCategoryList';
import useContentsLayout from '@bo-hooks/common/useContentsLayout';
import exhibitionApi from '@bo-apis/promotion/exhibitionApi';
import Grid from '@bo-components/common/grid/Grid';
import Search from '@bo-components/common/search/Search';
import SearchRow from '@bo-components/common/search/SearchRow';
import SearchItem from '@bo-components/common/search/SearchItem';
import SubsidiaryAutocomplete from '@bo-components/common/SubsidiaryAutocomplete';
import SearchUserAutocomplete from '@bo-components/common/components/user/SearchUserAutocomplete';
import {
  EXHIBITION_DIVISION_CODE,
  EXHIBITION_STATUS_CODE,
  EXHIBITION_TYPE_CODE,
  INTEGRATION_EXHIBITION_POPUP_OPTION,
  INTEGRATION_EXHIBITION_POPUP_OPTION_ONE_SEL,
  ISPE_CMPS_DIVISION_CODE,
  PERIOD_STATUS_CODE,
  SEARCH_KEYS,
  SEARCH_PERIODS,
  STAT_KEY,
} from '../../../options/exhibition/exhibitionConst';
import { PA_A11, PA_A12 } from '../message';

const periods = SEARCH_PERIODS;

const initPeriod = {
  from: periodFromDate({ months: 1 }),
  to: endOfToday(),
  period: periods[3],
};

const initialCheckboxItems = {
  exhibitionStatusItems: SEARCH_KEYS.exhibitionStatusItems,
  periodStatusItems: SEARCH_KEYS.periodStatusItems,
  exhibitionDivisionItems: SEARCH_KEYS.exhibitionDivisionItems,
  exhibitionTypeItems: SEARCH_KEYS.exhibitionTypeItems,
  useYnItems: SEARCH_KEYS.useYnItems,
  displayYnItems: SEARCH_KEYS.displayYnItems,
  mainDisplayYnItems: SEARCH_KEYS.mainDisplayYnItems,
};

const checkedFilter = (item) => item.filter(({ checked }) => checked);
const keyFilter = (item) => checkedFilter(item).map(({ key }) => key);
const ExhibitionSelectionPopup = ({ open, onClose, onConfirmClick, selectedExhibitionList, noMaxCount, isSingle, isOneSelect }) => {
  
  const { exhibitionCategoryList, standardCategoryList } = useCategoryList();
  const { laySearchOption } = useContentsLayout();
  const userInfo = useRecoilValue(userInfoState);
  const initialForm = useMemo(
    () => ({
      siteNumber: '',
      subsidiaryCompanyCode: '',
      searchKeywordType: SEARCH_KEYS.searchKeywordTypeItems[0],
      searchKeyword: '',
      exhibitionName: '',
      exhibitionNumber: '',
      periodType: SEARCH_KEYS.periodTypeItems[2],
      period: periods[3],
      from: format(startOfDay(initPeriod.from), 'yyyyMMddHHmmss'),
      to: format(endOfDay(initPeriod.to), 'yyyyMMddHHmmss'),
      standardCategory: standardCategoryList,
      mainCategory: [],
      subCategory: [],
      mdId: '',
      mdName: '',
      creatorId: userInfo?.userId || '',
      creatorName: userInfo?.userName || '',
      exhibitionStatus: [EXHIBITION_STATUS_CODE.TEMP_SAVE, EXHIBITION_STATUS_CODE.SUBMIT],
      periodStatus: [PERIOD_STATUS_CODE.BEFORE, PERIOD_STATUS_CODE.ING, PERIOD_STATUS_CODE.DONE],
      exhibitionDivision: isSingle
        ? [EXHIBITION_DIVISION_CODE.SINGLE]
        : [EXHIBITION_DIVISION_CODE.SINGLE, EXHIBITION_DIVISION_CODE.INTEGRATION, EXHIBITION_DIVISION_CODE.ALLIANCE],
      exhibitionType: [EXHIBITION_TYPE_CODE.SALE, EXHIBITION_TYPE_CODE.MD_PICK, EXHIBITION_TYPE_CODE.MD_CONTENTS],
      useYn: ['Y', 'N'],
      displayYn: ['Y', 'N'],
      mainDisplayYn: ['Y', 'N'],
    }),
    [standardCategoryList, userInfo?.userId, userInfo?.userName]
  );

  const [form, setForm] = useState({
    ...initialForm,
    mainCategory: exhibitionCategoryList,
    subCategory: exhibitionCategoryList,
  });
  const [checkboxSearchKeys, setCheckboxSearchKeys] = useState({ ...initialCheckboxItems });
  const [period, setPeriod] = useState({ ...initPeriod });
  const [exhibitionList, setExhibitionList] = useState([]);
  const [exhibitionTotal, setExhibitionTotal] = useState(0);

  const getCleanSearchForm = useCallback(
    (searchOption) => {
      return {
        ...searchOption,
        exhibitionName: form.searchKeywordType.key === 'exhibitionName' ? form.searchKeyword : '',
        exhibitionNumber: form.searchKeywordType.key === 'exhibitionNumber' ? form.searchKeyword : '',
        periodType: form.periodType.key,
        exhibitionDivision: isSingle
          ? [EXHIBITION_DIVISION_CODE.SINGLE]
          : [EXHIBITION_DIVISION_CODE.SINGLE, EXHIBITION_DIVISION_CODE.INTEGRATION, EXHIBITION_DIVISION_CODE.ALLIANCE],
        exhibitionStatus: [EXHIBITION_STATUS_CODE.SUBMIT],
        mainCategory:
          checkedFilter(form.mainCategory).length === exhibitionCategoryList.length ? [] : keyFilter(form.mainCategory),
        subCategory:
          checkedFilter(form.subCategory).length === exhibitionCategoryList.length ? [] : keyFilter(form.subCategory),
        standardCategory:
          searchOption.standardCategory &&
          checkedFilter(searchOption.standardCategory).length === standardCategoryList.length
            ? []
            : keyFilter(searchOption.standardCategory),
      };
    },
    [
      exhibitionCategoryList.length,
      form.mainCategory,
      form.periodType.key,
      form.searchKeyword,
      form.searchKeywordType.key,
      form.subCategory,
      standardCategoryList.length,
    ]
  );

  const setSearchOption = useCallback(
    (searchOption) => {
      cachedSearchOption.current = { ...searchOption };
      pageableSearchForm.current = getCleanSearchForm(searchOption);
      setForm((prev) => ({ ...prev, ...searchOption }));
    },
    [getCleanSearchForm]
  );

  const exhibitionGridRef = useRef(null);

  useEffect(() => {
    laySearchOption(setSearchOption, exhibitionGridRef);
  }, [laySearchOption, setSearchOption]);

  const pageableSearchForm = useRef({ ...getCleanSearchForm(initialForm) });
  const cachedSearchOption = useRef({ ...form });

  const { showAlertDialog } = useAlertDialog();

  // const { exhibitionOptions } = useExhibitionGridOptions({
  //   navigateWithSearchOption,
  //   handleToast,
  //   cachedSearchOption,
  //   exhibitionGridRef,
  // });

  const handleSearchFormChange = useEventCallback((v, data) => {
    setForm((prev) => ({ ...prev, [data.key]: v }));
  });

  const handlePeriodChange = ({ from, to, period, formattedFrom, formattedTo }) => {
    setPeriod({ from, to, period });
    setForm({
      ...form,
      from: formattedFrom,
      to: formattedTo,
    });
  };

  const handleSearchFormItemChange = (item) => {
    setForm((prev) => ({ ...prev, ...item }));
  };

  const handleClearClick = useCallback(() => {
    setForm({ ...initialForm, mainCategory: exhibitionCategoryList, subCategory: exhibitionCategoryList });
    setCheckboxSearchKeys({
      ...initialCheckboxItems,
    });
    setPeriod({ ...initPeriod });
  }, [exhibitionCategoryList, initialForm]);

  useEffect(() => {
    if (open) {
      handleClearClick();
    }
  }, [handleClearClick, open]);

  const resetCheckbox = useCallback(() => {
    const uncheckedSearchKeys = Object.entries(checkboxSearchKeys)
      .filter((searchKey) => checkedFilter(searchKey[1]).length === 0)
      .map((item) => item[0]);

    uncheckedSearchKeys.forEach((key) => {
      setCheckboxSearchKeys(
        {
          ...checkboxSearchKeys,
        },
        {
          [key]: checkboxSearchKeys[key].map((item) => (item.checked = true)),
        }
      );
    });

    // TODO. Reset Main Category MultiDropdown Checkbox
    if (checkedFilter(form.mainCategory).length === 0) {
      setForm({ ...form, mainCategory: exhibitionCategoryList });
    }
    if (checkedFilter(form.subCategory).length === 0) {
      setForm({ ...form, subCategory: exhibitionCategoryList });
    }
  }, [checkboxSearchKeys, form, exhibitionCategoryList]);

  const getExhibitionList = useCallback(
    async (pageable) => {
      const searchForm = { ...pageableSearchForm.current };
      const { result: { content: exhibitionList = [], totalElements: total } = {} } =
        await exhibitionApi.getExhibitionList(pageable, searchForm);
      // exhibitionName: form.searchKeywordType.key === 'exhibitionName' ? form.searchKeyword : '',
      // exhibitionNumber: form.searchKeywordType.key === 'exhibitionNumber' ? form.searchKeyword : '',
      // periodType: form.periodType.key,
      // exhibitionDivision: [EXHIBITION_DIVISION_CODE.SINGLE],
      // exhibitionStatus: [EXHIBITION_STATUS_CODE.SUBMIT],
      // mainCategory:
      //   checkedFilter(form.mainCategory).length === exhibitionCategoryList.length
      //     ? []
      //     : keyFilter(form.mainCategory),
      // subCategory:
      //   checkedFilter(form.subCategory).length === exhibitionCategoryList.length ? [] : keyFilter(form.subCategory),
      // });
      const formattedExhibitions = exhibitionList.map((exhibition) => {
        return {
          ...exhibition,
          displaySeq: '999',
          exposeName: '',
          topContentsExposeYn: 'Y',
          // divisionName: '기획전',
          divisionCode: ISPE_CMPS_DIVISION_CODE.EXHIBITION,
          standardCategoryName: exhibition.standardCategory?.map((ctg) => ctg.standardCategoryName).join(','),
          exhibitionType: exhibition.divisionCode === EXHIBITION_DIVISION_CODE.SINGLE ? exhibition.typeName : '',
          mainDisplayYn:
            exhibition.divisionCode === EXHIBITION_DIVISION_CODE.INTEGRATION ? 'N' : exhibition.mainDisplayYn,
          fromTo: `${format(parse(exhibition.startDate, 'yyyyMMddHHmmss', new Date()), 'yyyy-MM-dd')} ~ ${format(
            parse(exhibition.endDate, 'yyyyMMddHHmmss', new Date()),
            'yyyy-MM-dd'
          )}`,
        };
      });
      const selectedSpeNoList = selectedExhibitionList?.map(({ speNo }) => speNo);
      const filteredExhibitionList = formattedExhibitions.filter(
        (exhibition) => !selectedSpeNoList?.includes(exhibition.speNo)
      );
      setExhibitionList(filteredExhibitionList);
      setExhibitionTotal(total);
    },
    [selectedExhibitionList]
  );

  const handlePageableExhibitionListSearch = useCallback(
    async (pageIndex, pageSize, sort = {}) => {
      await getExhibitionList({ size: pageSize, page: pageIndex, sort });
    },
    [getExhibitionList]
  );

  const handleSubsidiaryCompanySelect = ({ siteNumber, subsidiaryCompanyCode }) => {
    setForm((prev) => ({ ...prev, siteNumber, subsidiaryCompanyCode }));
  };

  const handleSearchClick = useCallback(async () => {
    cachedSearchOption.current = { ...form };
    pageableSearchForm.current = getCleanSearchForm(form);
    exhibitionGridRef.current.setPageIndex(0);
    exhibitionGridRef.current.clearSort();
    resetCheckbox();

    await getExhibitionList({
      size: exhibitionGridRef.current.getPageSize(),
      page: 0,
      sort: exhibitionGridRef.current.getPageSort(),
    });
  }, [form, getCleanSearchForm, getExhibitionList, resetCheckbox]);

  const handleConfirmClick = () => {
    if (exhibitionGridRef.current?.checkedRows().length === 0) {
      showAlertDialog(PA_A11);
      return;
    }
    if (!noMaxCount && exhibitionGridRef.current?.checkedRows().length + selectedExhibitionList.length > 20) {
      showAlertDialog(PA_A12);
      return;
    }
    onConfirmClick(exhibitionGridRef.current?.checkedRows());
    onClose();
  };

  return (
    <Popup
      open={open}
      onClose={onClose}
      header="기획전추가"
      width="520px"
      confirmButtonText="확인"
      onConfirmClick={handleConfirmClick}
      onCancelClick={onClose}
    >
      <FlexBoxColumn width={'100%'}>
        <Search onClearClick={handleClearClick} onSearchClick={handleSearchClick}>
          <SearchRow>
            <SearchItem label="사이트/계열사">
              <FlexBox width={'320px'}>
                <SubsidiaryAutocomplete
                  siteNumber={form.siteNumber}
                  subsidiaryCompanyCode={form.subsidiaryCompanyCode}
                  onSelect={handleSubsidiaryCompanySelect}
                  filterByJobGroup={false}
                />
              </FlexBox>
            </SearchItem>
          </SearchRow>
          <SearchRow>
            <SearchItem label="검색어">
              <Dropdown
                items={SEARCH_KEYS.searchKeywordTypeItems}
                value={form.searchKeywordType}
                onChange={(searchKeywordType) => handleSearchFormItemChange({ searchKeywordType })}
                width="124px"
              />
              <Spacing right={6} />
              <Input
                width="190px"
                value={form.searchKeyword}
                data-key="searchKeyword"
                onChange={handleSearchFormChange}
              />
            </SearchItem>
          </SearchRow>
          <SearchRow>
            <SearchItem label="기간">
              <Dropdown
                items={SEARCH_KEYS.periodTypeItems}
                value={form.periodType}
                onChange={(periodType) => handleSearchFormItemChange({ periodType })}
                width="124px"
              />
              <Spacing right={6} />
              <PeriodSearch
                data-key="period"
                fromDate={period.from}
                toDate={period.to}
                period={period.period}
                periods={periods}
                defaultPeriod={period.period}
                onChange={handlePeriodChange}
              />
            </SearchItem>
          </SearchRow>
          <SearchRow>
            <SearchItem label="메인카테고리">
              <CheckboxGroupDropdown
                data-key="mainCategory"
                itemList={form.mainCategory}
                onChange={handleSearchFormChange}
                width={'180px'}
              />
            </SearchItem>
            <SearchItem label="서브카테고리">
              <FlexBox width={'180px'}>
                <CheckboxGroupDropdown
                  data-key="subCategory"
                  itemList={form.subCategory}
                  onChange={handleSearchFormChange}
                  width={'180px'}
                />
              </FlexBox>
            </SearchItem>
          </SearchRow>
          <SearchRow>
            <SearchItem label="사용자(담당MD)">
              <FlexBox width={'180px'}>
                <SearchUserAutocomplete
                  onSelect={({ userId, userName }) => handleSearchFormItemChange({ mdId: userId, mdName: userName })}
                  userId={form.mdId || ''}
                  userName={form.mdName || ''}
                />
              </FlexBox>
            </SearchItem>
            <SearchItem label="사용자(등록자)">
              <FlexBox width={'180px'}>
                <SearchUserAutocomplete
                  onSelect={({ userId, userName }) =>
                    handleSearchFormItemChange({ creatorId: userId, creatorName: userName })
                  }
                  userId={form.creatorId || ''}
                  userName={form.creatorName || ''}
                />
              </FlexBox>
            </SearchItem>
          </SearchRow>
          <SearchRow>
            <SearchItem label="기획전유형">
              <AllCheckboxGroup
                data-key={STAT_KEY.EXHIBITION_TYPE}
                checked={form.exhibitionType}
                itemList={SEARCH_KEYS.exhibitionTypeItems}
                onChange={handleSearchFormChange}
              />
            </SearchItem>
            <SearchItem label="기간상태">
              <AllCheckboxGroup
                data-key={STAT_KEY.PERIOD_STATUS}
                checked={form.periodStatus}
                itemList={SEARCH_KEYS.periodStatusItems}
                onChange={handleSearchFormChange}
              />
            </SearchItem>
          </SearchRow>
          <SearchRow>
            <SearchItem label="사용상태">
              <AllCheckboxGroup
                data-key={STAT_KEY.USE_YN}
                checked={form.useYn}
                itemList={SEARCH_KEYS.useYnItems}
                onChange={handleSearchFormChange}
              />
            </SearchItem>
            <SearchItem label="전시상태">
              <AllCheckboxGroup
                data-key={STAT_KEY.DISPLAY_YN}
                checked={form.displayYn}
                itemList={SEARCH_KEYS.displayYnItems}
                onChange={handleSearchFormChange}
              />
            </SearchItem>
          </SearchRow>
        </Search>
        <Spacing bottom={30} width="100%" />
        <Grid
          title="기획전목록"
          options={ isOneSelect ? INTEGRATION_EXHIBITION_POPUP_OPTION_ONE_SEL : INTEGRATION_EXHIBITION_POPUP_OPTION}
          data={exhibitionList}
          total={exhibitionTotal}
          initRow={{ sortSequence: 999, useYn: 'Y' }}
          onSearch={handlePageableExhibitionListSearch}
          ref={exhibitionGridRef}
        />
      </FlexBoxColumn>
    </Popup>
  );
};

ExhibitionSelectionPopup.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirmClick: PropTypes.func,
  selectedExhibitionList: PropTypes.array,
  noMaxCount: PropTypes.bool,
  isSingle: PropTypes.bool,
  isOneSelect: PropTypes.bool,
};

ExhibitionSelectionPopup.defaultProps = {
  open: false,
  onClose: () => {},
  onConfirmClick: () => {},
  selectedExhibitionList: [],
  noMaxCount: false,
  isSingle: true,
};

export default ExhibitionSelectionPopup;
